import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import {
  DogPage,
  DogFactsBar,
  DogProfileActions
} from "components/Dogs/DogComponents.js";
import SEO from "components/SEO/SEO.js";
import DogGallery from "./DogGallery";

const agilityIcon = require("../../assets/img/icon-agility.png");
const discIcon = require("../../assets/img/icon-disc.png");
const dockDivingIcon = require("../../assets/img/icon-dockdiving.png");
const flyballIcon = require("../../assets/img/icon-flyball.png");
const stackImage = require("../../assets/img/dogs/cirilla/stack5.jpg");
const pedigreeImage = require("../../assets/img/dogs/cirilla/pedigree.jpg");
const clearances = [
  {
    id: "cirilla-pennhip-2019",
    label: "PennHIP Report",
    shortLabel: "PennHIP",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "December 2019",
    image: require("../../assets/img/dogs/cirilla/clearance1.jpg"),
    alt: "Cirilla PennHIP report from December 2019"
  },
  {
    id: "cirilla-ofa-orthopedic-results-2019",
    label: "OFA Orthopedic Results",
    shortLabel: "OFA Results",
    category: "Orthopedic",
    detailLabel: "Hips, Elbows, Shoulders",
    date: "December 2019",
    image: require("../../assets/img/dogs/cirilla/clearance2.jpg"),
    alt: "Cirilla OFA orthopedic results from December 2019"
  },
  {
    id: "cirilla-optimal-known-disorders-2020",
    label: "Optimal Selection DNA Report",
    shortLabel: "Optimal DNA",
    category: "Genetic",
    detailLabel: "Known Disorders",
    date: "January 2020",
    image: require("../../assets/img/dogs/cirilla/clearance3.jpg"),
    alt: "Cirilla Optimal Selection DNA report from January 2020"
  },
  {
    id: "cirilla-optimal-additional-tests-2020",
    label: "Optimal Selection Additional Tests",
    shortLabel: "Additional DNA",
    category: "Genetic",
    detailLabel: "Additional DNA Tests",
    date: "January 2020",
    image: require("../../assets/img/dogs/cirilla/clearance4.jpg"),
    alt: "Cirilla Optimal Selection additional DNA tests from January 2020"
  },
  {
    id: "cirilla-optimal-mdr1-2020",
    label: "Optimal Selection MDR1 Report",
    shortLabel: "MDR1",
    category: "Genetic",
    detailLabel: "Pharmacogenetics",
    date: "January 2020",
    image: require("../../assets/img/dogs/cirilla/clearance5.jpg"),
    alt: "Cirilla Optimal Selection MDR1 pharmacogenetics report from January 2020"
  },
  {
    id: "cirilla-embark-dna-2018",
    label: "Embark DNA Test Report",
    shortLabel: "Embark DNA",
    category: "Genetic",
    detailLabel: "DNA Panel",
    date: "December 2018",
    image: require("../../assets/img/dogs/cirilla/clearance6.jpg"),
    alt: "Cirilla Embark DNA test report from December 2018"
  }
];

const galleryImages = [
  require("../../assets/img/dogs/cirilla/agility1.jpg"),
  require("../../assets/img/dogs/cirilla/agility2.jpg"),
  require("../../assets/img/dogs/cirilla/agility3.jpg"),
  require("../../assets/img/dogs/cirilla/agility5.jpg"),
  require("../../assets/img/dogs/cirilla/disc2.jpg"),
  require("../../assets/img/dogs/cirilla/disc3.jpg"),
  require("../../assets/img/dogs/cirilla/disc4.jpg"),
  require("../../assets/img/dogs/cirilla/disc5.jpg"),
  require("../../assets/img/dogs/cirilla/dock1.jpg"),
  require("../../assets/img/dogs/cirilla/dock2.jpg"),
  require("../../assets/img/dogs/cirilla/flyball1.jpg"),
  require("../../assets/img/dogs/cirilla/reg1.jpg"),
  require("../../assets/img/dogs/cirilla/reg3.jpg"),
  require("../../assets/img/dogs/cirilla/reg5.jpg"),
  require("../../assets/img/dogs/cirilla/reg10.jpg"),
  require("../../assets/img/dogs/cirilla/reg12.jpg"),
  require("../../assets/img/dogs/cirilla/reg13.jpg"),
  require("../../assets/img/dogs/cirilla/reg9.jpg")
];

const cirillaProfile = {
  name: "Cirilla",
  registeredName: "Infinity Runners Something Special AX AXJ DM",
  about: "Ciri is a red merle border collie bred by Zuza Felczak in Poland and raised and trained by Adriana. She is a very excitable dog with amazing physical abilities and high drive. Ciri was unfortunately never able to get pregnant, so she will be spayed and continue her sports career.",
  facts: [
    { label: "Breed", value: "Border Collie" },
    { label: "Born", value: "9/16/18" },
    { label: "Height", value: '19"' },
    { label: "Weight", value: "32lbs" }
  ],
  temperament: "Cirilla is the energizer bunny of the house. She is often running around and always looking for an opportunity to play. She enjoys playing with dogs that she knows well and is indifferent to unfamiliar dogs, even when they approach her while working. She loves everyone she meets and enjoys trying to jump into their arms whether or not they are expecting it. Ciri has both an independent and cuddly nature. She is fine on her own, but also enjoys curling up at Adriana's feet every night.",
  athleticAbility: "Ciri puts 110% effort into everything she does. Her first jumps off of the dock were over 20 feet. She has a lot of power and speed and can be difficult to rein in, but she is amazing once control is added. She works equally well for both food and toys. She can work in any environment, no matter how busy or noisy. She enjoys herding the other house dogs, but has not had interest in stock. She is very high drive and high energy.",
  stats: 'Born on 9/16/18. 19" tall. 32lbs. AKC & FCI registered.',
  video: {
    title: "Cirilla agility video",
    src: "https://www.youtube.com/embed/29hWdOdyDfU"
  }
};

const healthGroups = [
  {
    title: "Orthopedic & Screening",
    items: [
      { label: "Hips", value: "OFA Good, PennHip DI .34" },
      { label: "Elbows", value: "Normal*" },
      { label: "Shoulders", value: "OFA Normal" },
      { label: "Eyes", value: "Normal (2020)" }
    ],
    note: "* OFA Prelim"
  },
  {
    title: "DNA Panel",
    items: [
      { label: "CEA", value: "Clear" },
      { label: "DM", value: "Clear" },
      { label: "SN", value: "Clear" },
      { label: "TNS", value: "Clear" },
      { label: "IGS", value: "Clear" },
      { label: "NCL5", value: "Clear" },
      { label: "G/G", value: "Clear" },
      { label: "DH", value: "Clear" },
      { label: "EOD", value: "Carrier" }
    ]
  }
];

const competitionHighlights = [
  {
    sport: "Agility",
    icon: agilityIcon,
    groups: [
      {
        label: "Progress",
        items: ["Excellent Jumpers and Standard in AKC by her 2nd birthday"]
      },
      {
        label: "Speed",
        items: ["First Excellent Jumpers Q at 7.54 YPS", "Multiple runs over 7 YPS"]
      },
      {
        label: "Major Result",
        items: ['2021 US Open Masters Series 5th in a large 20" class']
      }
    ]
  },
  {
    sport: "Dock Diving",
    icon: dockDivingIcon,
    groups: [
      {
        label: "Personal Bests",
        items: ["Distance: 25ft 3in", "Hydrodash: 14.768", "First dock jump: 22ft 6in"]
      },
      {
        label: "Major Result",
        items: ["2020 Atlantic Coast Master Hydrodash Winner"]
      }
    ]
  },
  {
    sport: "Disc",
    icon: discIcon,
    groups: [
      {
        label: "Venues",
        items: ["UpDog", "Local Toss and Fetch"]
      }
    ]
  },
  {
    sport: "Flyball",
    icon: flyballIcon,
    groups: [
      {
        label: "Personal Bests",
        items: ["PB: 3.877", "Average: 3.9"]
      }
    ]
  }
];

function DogSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="dog-detail-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function CirillaPage() {
  const hasVideo = Boolean(cirillaProfile.video && cirillaProfile.video.src);

  return (
    <>
      <SEO
        title="Cirilla | Border Collie Sport Dog"
        description="Meet Cirilla, a red merle border collie in the Endgame Sport Dogs family with accomplishments in agility, dock diving, disc, and flyball."
        path="/dogs/cirilla"
      />
      <DogPage
        headerBg="cirilla"
        title={cirillaProfile.name}
        subtitle={cirillaProfile.registeredName}
        pageClassName="dog-detail-page dog-cirilla-page"
      >
        <section className="dog-facts-wrap" aria-label="Cirilla at a glance">
          <Container>
            <DogFactsBar facts={cirillaProfile.facts} />
          </Container>
        </section>

        <section className="dog-detail-section dog-about-section">
          <Container>
            <div className="dog-about-layout">
              <div className="dog-about-copy">
                <div className="dog-back-link">
                  <Link to="/dogs">
                    <ArrowLeft aria-hidden="true" size={14} strokeWidth={2.2} /> Back to Dogs
                  </Link>
                </div>
                <DogSectionHeading title={`About ${cirillaProfile.name}`} />
                <p>{cirillaProfile.about}</p>
              </div>
              <figure className="dog-stack-figure">
                <img alt="Cirilla stacked in profile" src={stackImage} />
                <figcaption>{cirillaProfile.stats}</figcaption>
              </figure>
            </div>

            <div className="dog-health-spec-panel site-bg-light-blue">
              <div className="dog-health-panel-heading">
                <h2>Health & Genetics</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-health-results">
                {healthGroups.map(group => (
                  <section
                    className={`dog-health-spec-group${group.title === "DNA Panel" ? " dog-health-dna-group" : ""}`}
                    key={group.title}
                  >
                    <h3>{group.title}</h3>
                    <dl>
                      {group.items.map(item => (
                        <div className="dog-health-result" key={`${group.title}-${item.label}`}>
                          <span className="dog-health-check" aria-hidden="true">✓</span>
                          <dt>{item.label}</dt>
                          <dd>{item.value}</dd>
                        </div>
                      ))}
                    </dl>
                    {group.note && <p>{group.note}</p>}
                  </section>
                ))}
              </div>
              <section className="dog-health-spec-group dog-health-actions-group">
                <h3>Documents</h3>
                <DogProfileActions
                  dogName={cirillaProfile.name}
                  pedigreeImage={pedigreeImage}
                  pedigreeAlt="Cirilla pedigree"
                  clearances={clearances}
                  className="dog-profile-actions"
                />
              </section>
            </div>

            <div className="dog-traits-layout">
              <article>
                <h2>Temperament</h2>
                <span aria-hidden="true"></span>
                <p>{cirillaProfile.temperament}</p>
              </article>
              <article>
                <h2>Athletic Ability</h2>
                <span aria-hidden="true"></span>
                <p>{cirillaProfile.athleticAbility}</p>
              </article>
            </div>
          </Container>
        </section>

        <div className="dog-performance-wrap">
          {hasVideo && (
            <section className="dog-detail-section dog-video-section">
              <Container>
                <DogSectionHeading eyebrow={`${cirillaProfile.name} in Action`} title="Agility highlights" />
                <div className="dog-video-feature">
                  <div className="dog-video-embed">
                    <h3>{cirillaProfile.video.title}</h3>
                    <div className="video-frame">
                      <iframe
                        src={cirillaProfile.video.src}
                        title={cirillaProfile.video.title}
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                  <div className="dog-video-caption">
                    <p>Cirilla made it into Excellent Jumpers and Standard in AKC by her second birthday. Her first Excellent Jumpers Q was 7.54 YPS, she has had multiple runs over 7 YPS, and she finished 5th in Masters Series in a large 20 inch class at the 2021 US Open.</p>
                  </div>
                </div>
              </Container>
            </section>
          )}

          <section className="dog-detail-section dog-competition-section">
            <Container>
              <div className="dog-health-panel-heading dog-competition-panel-heading">
                <h2>Competition Highlights</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-competition-grid">
                {competitionHighlights.map(group => (
                  <section className="dog-sport-highlight" key={group.sport}>
                    <h3>
                      <span className="dog-sport-highlight-icon" aria-hidden="true">
                        <img alt="" src={group.icon} />
                      </span>
                      {group.sport}
                    </h3>
                    {group.groups.map(section => (
                      <div className="dog-sport-highlight-group" key={`${group.sport}-${section.label}`}>
                        <h4>{section.label}</h4>
                        <ul>
                          {section.items.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </Container>
          </section>
        </div>

        <section className="dog-detail-section dog-gallery-section site-bg-dark-navy">
          <Container>
            <DogSectionHeading title="Photo Gallery" />
          </Container>
          <DogGallery images={galleryImages} />
        </section>
      </DogPage>
    </>
  );
}

export default CirillaPage;
